import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { FileText, TrendingUp, Users, Clock, PieChart, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import LogoLoader from '@/components/LogoLoader';

const Reports = () => {
  const { token } = useAuth();
  const [poTracker, setPoTracker] = useState([]);
  const [payrollData, setPayrollData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [mentoringData, setMentoringData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [profitPeriodStart, setProfitPeriodStart] = useState('');
  const [profitPeriodEnd, setProfitPeriodEnd] = useState('');
  const [mentoringPeriodStart, setMentoringPeriodStart] = useState('');
  const [mentoringPeriodEnd, setMentoringPeriodEnd] = useState('');
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedSenior, setSelectedSenior] = useState('all');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      setInitializing(true);
      try {
        await Promise.all([loadPOTracker(), loadProjects(), loadConsultants()]);
      } finally {
        setInitializing(false);
      }
    };

    initialize();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await api.get('/projects', token);
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const loadConsultants = async () => {
    try {
      const data = await api.get('/consultants', token);
      setConsultants(data.filter(c => c.mentoring_rate > 0));
    } catch (error) {
      console.error('Failed to load consultants');
    }
  };

  const loadPOTracker = async () => {
    try {
      const data = await api.get('/reports/po-tracker', token);
      setPoTracker(data);
    } catch (error) {
      toast.error('Failed to load PO tracker');
    }
  };

  const loadPayrollReport = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Please select period start and end dates');
      return;
    }

    setLoading(true);
    try {
      const data = await api.get(`/reports/payroll?period_start=${periodStart}&period_end=${periodEnd}`, token);
      setPayrollData(data);
      toast.success('Payroll report loaded');
    } catch (error) {
      toast.error('Failed to load payroll report');
    } finally {
      setLoading(false);
    }
  };

  const loadProfitAnalysis = async () => {
    if (!profitPeriodStart || !profitPeriodEnd) {
      toast.error('Please select period start and end dates');
      return;
    }

    setLoading(true);
    try {
      const url = selectedProject === 'all' 
        ? `/reports/profit-analysis?period_start=${profitPeriodStart}&period_end=${profitPeriodEnd}`
        : `/reports/profit-analysis?period_start=${profitPeriodStart}&period_end=${profitPeriodEnd}&project_id=${selectedProject}`;
      const data = await api.get(url, token);
      setProfitData(data);
      toast.success('Profit analysis loaded');
    } catch (error) {
      toast.error('Failed to load profit analysis');
    } finally {
      setLoading(false);
    }
  };

  const loadMentoringCompensation = async () => {
    if (!mentoringPeriodStart || !mentoringPeriodEnd) {
      toast.error('Please select period start and end dates');
      return;
    }

    setLoading(true);
    try {
      const url = selectedSenior === 'all' 
        ? `/reports/mentoring-compensation?period_start=${mentoringPeriodStart}&period_end=${mentoringPeriodEnd}`
        : `/reports/mentoring-compensation?period_start=${mentoringPeriodStart}&period_end=${mentoringPeriodEnd}&senior_consultant_id=${selectedSenior}`;
      const data = await api.get(url, token);
      setMentoringData(data);
      toast.success('Mentoring compensation loaded');
    } catch (error) {
      toast.error('Failed to load mentoring compensation');
    } finally {
      setLoading(false);
    }
  };

  const generateMentoringExpenses = async () => {
    if (!mentoringPeriodStart || !mentoringPeriodEnd) {
      toast.error('Please select period start and end dates first');
      return;
    }

    if (mentoringData.length === 0) {
      toast.error('Please generate the mentoring compensation report first');
      return;
    }

    setLoading(true);
    try {
      const url = selectedSenior === 'all' 
        ? `/reports/mentoring-compensation/generate-expenses?period_start=${mentoringPeriodStart}&period_end=${mentoringPeriodEnd}`
        : `/reports/mentoring-compensation/generate-expenses?period_start=${mentoringPeriodStart}&period_end=${mentoringPeriodEnd}&senior_consultant_id=${selectedSenior}`;
      
      const result = await api.post(url, {}, token);
      const totalAmount = result.details.reduce((sum, exp) => sum + exp.amount, 0);
      toast.success(`Successfully generated ${result.expenses_created} expense(s) for mentoring compensation totaling $${totalAmount.toLocaleString()}`);
    } catch (error) {
      console.error('Generate expenses error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate expenses');
    } finally {
      setLoading(false);
    }
  };

  const generateConsultantPaymentExpenses = async () => {
    if (!periodStart || !periodEnd) {
      toast.error('Please select period start and end dates first');
      return;
    }

    if (payrollData.length === 0) {
      toast.error('Please generate the payroll report first');
      return;
    }

    setLoading(true);
    try {
      const result = await api.post(`/reports/payroll/generate-expenses?period_start=${periodStart}&period_end=${periodEnd}`, {}, token);
      toast.success(`Successfully generated ${result.expenses_created} expense(s) for consultant payments totaling $${result.total_amount.toLocaleString()}`);
    } catch (error) {
      console.error('Generate consultant payment expenses error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate expenses');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for profit analysis
  const totalRevenue = profitData.reduce((sum, item) => sum + item.revenue, 0);
  const totalCost = profitData.reduce((sum, item) => sum + item.total_cost, 0);
  const totalProfit = profitData.reduce((sum, item) => sum + item.profit, 0);
  const overallMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100) : 0;

  if (initializing) {
    return <LogoLoader message="Loading reports data..." />;
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {loading && <LogoLoader message="Processing report request..." fullscreen />}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
        <p className="text-gray-600 mt-1">Analytics and insights for your accounting operations</p>
      </div>

      <Tabs defaultValue="po-tracker" className="w-full">
        <TabsList className="grid w-full max-w-4xl grid-cols-4">
          <TabsTrigger value="po-tracker">PO Tracker</TabsTrigger>
          <TabsTrigger value="payroll">Payroll Summary</TabsTrigger>
          <TabsTrigger value="profit">Profit Analysis</TabsTrigger>
          <TabsTrigger value="mentoring">Mentoring Compensation</TabsTrigger>
        </TabsList>

        {/* PO Tracker Report */}
        <TabsContent value="po-tracker" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-utis-forest" />
                Purchase Order Tracker
              </CardTitle>
              <CardDescription>Monitor PO consumption and capacity alerts</CardDescription>
            </CardHeader>
            <CardContent>
              {poTracker.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No purchase orders to track yet.
                </div>
              ) : (
                <div className="space-y-6">
                  {poTracker.map((po) => (
                    <div key={po.po_id} className={`p-6 rounded-lg border-2 ${po.alert ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{po.po_number}</h3>
                          {po.alert && (
                            <Badge variant="destructive" className="mt-1">⚠️ Capacity Alert</Badge>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Hours Tracking */}
                        {po.hours_cap && (
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">Hours Consumed</span>
                              <span className="text-sm font-bold text-gray-900">
                                {po.hours_consumed.toFixed(1)} / {po.hours_cap} hrs
                              </span>
                            </div>
                            <Progress value={po.hours_pct} className="h-3" />
                            <div className="flex justify-between mt-1 text-xs text-gray-600">
                              <span>{po.hours_pct.toFixed(1)}% used</span>
                              <span>{po.hours_remaining?.toFixed(1) || 0} hrs remaining</span>
                            </div>
                          </div>
                        )}

                        {/* Value Tracking */}
                        {po.value_cap && (
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm font-medium text-gray-700">Value Consumed</span>
                              <span className="text-sm font-bold text-gray-900">
                                ${po.value_consumed.toLocaleString()} / ${po.value_cap.toLocaleString()}
                              </span>
                            </div>
                            <Progress value={po.value_pct} className="h-3" />
                            <div className="flex justify-between mt-1 text-xs text-gray-600">
                              <span>{po.value_pct.toFixed(1)}% used</span>
                              <span>${po.value_remaining?.toLocaleString() || 0} remaining</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {!po.hours_cap && !po.value_cap && (
                        <div className="text-center py-4 text-gray-500">
                          No caps defined for this PO (unlimited)
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payroll Summary Report */}
        <TabsContent value="payroll" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-utis-forest" />
                Payroll Summary
              </CardTitle>
              <CardDescription>Calculate consultant and vendor payables for a period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Period Start</Label>
                    <Input
                      type="date"
                      value={periodStart}
                      onChange={(e) => setPeriodStart(e.target.value)}
                      placeholder="09-01-2025"
                    />
                  </div>
                  <div>
                    <Label>Period End</Label>
                    <Input
                      type="date"
                      value={periodEnd}
                      onChange={(e) => setPeriodEnd(e.target.value)}
                      placeholder="09-30-2025"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={loadPayrollReport}
                      disabled={loading}
                      className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white"
                      data-testid="generate-payroll-report"
                    >
                      {loading ? 'Loading...' : 'Generate Report'}
                    </Button>
                  </div>
                </div>

                {payrollData.length > 0 && (
                  <div className="mt-6">
                    {/* Generate Expenses Button */}
                    <div className="mb-4">
                      <Button
                        onClick={generateConsultantPaymentExpenses}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        {loading ? 'Generating...' : 'Generate Expenses for Consultant Payments'}
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically create Direct expenses for consultant payments
                      </p>
                    </div>

                    <div className="bg-utis-sage/10 border border-utis-sage rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-900">Total Payroll</span>
                        <span className="text-2xl font-bold text-green-900">
                          ${payrollData
                            .reduce((sum, item) => sum + (item.amount || 0), 0)
                            .toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-utis-forest">Total Hours</span>
                        <span className="text-lg font-semibold text-utis-forest">
                          {payrollData
                            .reduce((sum, item) => sum + (item.hours || 0), 0)
                            .toFixed(1)} hrs
                        </span>
                      </div>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Payment Entity</TableHead>
                          <TableHead>Hours</TableHead>
                          <TableHead>Pay Rate</TableHead>
                          <TableHead>Amount Due</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payrollData.map((item) => {
                          const isVendor = item.entity_type === 'Vendor';
                          const rowKey = isVendor
                            ? `vendor-${item.vendor_id}`
                            : `consultant-${item.consultant_id}`;
                          const displayName = isVendor
                            ? item.vendor_name
                            : item.consultant_name;
                          const hours = Number(item.hours || 0).toFixed(1);
                          const amount = Number(item.amount || 0).toLocaleString();
                          const consultants = Array.isArray(item.consultants)
                            ? item.consultants
                            : [];
                          const rateLabel = isVendor
                            ? item.effective_pay_rate
                              ? `$${Number(item.effective_pay_rate).toFixed(2)}/hr avg`
                              : '—'
                            : `$${item.pay_rate}/hr`;

                          return (
                            <TableRow key={rowKey}>
                              <TableCell className="align-top">
                                <div className="flex flex-col gap-2">
                                  <div>
                                    <div className="font-medium text-gray-900">{displayName}</div>
                                    <div className="mt-1 flex flex-wrap items-center gap-2">
                                      <Badge variant="outline" className={isVendor ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                                        {isVendor ? 'Vendor-supplied (C2C)' : 'Individual (1099)'}
                                      </Badge>
                                      {isVendor && consultants.length > 0 && (
                                        <span className="text-xs text-gray-500">
                                          {consultants.length} consultant{consultants.length > 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {isVendor && consultants.length > 0 && (
                                    <ul className="ml-4 list-disc space-y-1 text-xs text-gray-600">
                                      {consultants.map((consultant) => (
                                        <li key={consultant.consultant_id}>
                                          {consultant.consultant_name} — {consultant.hours.toFixed(1)}h @ ${consultant.pay_rate}/hr
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="align-top">{hours} hrs</TableCell>
                              <TableCell className="align-top">{rateLabel}</TableCell>
                              <TableCell className="align-top font-bold text-utis-forest">${amount}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-900">
                        <strong>Note:</strong> This is a payroll summary for approved time entries. Vendor-supplied (C2C) consultants are grouped by vendor so a single payment can be issued against their combined invoice. Use this data to process payments through your payment system.
                      </p>
                    </div>
                  </div>
                )}

                {payrollData.length === 0 && periodStart && periodEnd && !loading && (
                  <div className="text-center py-12 text-gray-500">
                    No approved time entries found for the selected period.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profit Analysis Tab */}
        <TabsContent value="profit" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <PieChart className="w-5 h-5 mr-2 text-utis-forest" />
                Profit & Loss Analysis
              </CardTitle>
              <CardDescription>Analyze profitability by project: Revenue - Costs = Profit</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Period Start</Label>
                    <Input
                      type="date"
                      value={profitPeriodStart}
                      onChange={(e) => setProfitPeriodStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Period End</Label>
                    <Input
                      type="date"
                      value={profitPeriodEnd}
                      onChange={(e) => setProfitPeriodEnd(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Project Filter</Label>
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Projects</SelectItem>
                        {projects.filter(p => p.is_active).map(project => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={loadProfitAnalysis}
                      disabled={loading}
                      className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white"
                    >
                      {loading ? 'Loading...' : 'Generate Report'}
                    </Button>
                  </div>
                </div>

                {profitData.length > 0 && (
                  <div>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                      <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-900">Total Revenue</p>
                              <p className="text-2xl font-bold text-blue-900 mt-2">
                                ${totalRevenue.toLocaleString()}
                              </p>
                            </div>
                            <DollarSign className="w-8 h-8 text-blue-700" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-orange-200 bg-orange-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-orange-900">Total Costs</p>
                              <p className="text-2xl font-bold text-orange-900 mt-2">
                                ${totalCost.toLocaleString()}
                              </p>
                            </div>
                            <TrendingUp className="w-8 h-8 text-orange-700" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className={`border-2 ${totalProfit >= 0 ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className={`text-sm font-medium ${totalProfit >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                                Net Profit
                              </p>
                              <p className={`text-2xl font-bold mt-2 ${totalProfit >= 0 ? 'text-green-900' : 'text-red-900'}`}>
                                ${totalProfit.toLocaleString()}
                              </p>
                            </div>
                            <PieChart className={`w-8 h-8 ${totalProfit >= 0 ? 'text-green-700' : 'text-red-700'}`} />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-utis-sage bg-utis-sage/10">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-utis-forest">Profit Margin</p>
                              <p className="text-2xl font-bold text-utis-forest mt-2">
                                {overallMargin.toFixed(1)}%
                              </p>
                            </div>
                            <TrendingUp className="w-8 h-8 text-utis-forest" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Detailed Table */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Project-wise Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Project</TableHead>
                              <TableHead>Client</TableHead>
                              <TableHead>Revenue</TableHead>
                              <TableHead>Consultant Cost</TableHead>
                              <TableHead>Expenses</TableHead>
                              <TableHead>Total Cost</TableHead>
                              <TableHead>Profit</TableHead>
                              <TableHead>Margin %</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {profitData.map((item) => (
                              <TableRow key={item.project_id}>
                                <TableCell className="font-medium">{item.project_name}</TableCell>
                                <TableCell>{item.client_name}</TableCell>
                                <TableCell className="text-blue-700 font-semibold">
                                  ${item.revenue.toLocaleString()}
                                </TableCell>
                                <TableCell className="text-orange-700">
                                  ${item.consultant_cost.toLocaleString()}
                                </TableCell>
                                <TableCell className="text-gray-600">
                                  ${item.non_billable_expenses.toLocaleString()}
                                </TableCell>
                                <TableCell className="text-orange-900 font-semibold">
                                  ${item.total_cost.toLocaleString()}
                                </TableCell>
                                <TableCell className={`font-bold ${item.profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                  ${item.profit.toLocaleString()}
                                </TableCell>
                                <TableCell>
                                  <Badge 
                                    variant="outline" 
                                    className={item.profit_margin >= 20 ? 'bg-green-100 text-green-800' : item.profit_margin >= 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}
                                  >
                                    {item.profit_margin.toFixed(1)}%
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>

                        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2">Revenue Sources</h4>
                            <div className="space-y-1 text-sm text-blue-800">
                              <div className="flex justify-between">
                                <span>Invoiced Amount:</span>
                                <span className="font-semibold">${totalRevenue.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>

                          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <h4 className="font-semibold text-orange-900 mb-2">Cost Breakdown</h4>
                            <div className="space-y-1 text-sm text-orange-800">
                              <div className="flex justify-between">
                                <span>Consultant Pay:</span>
                                <span className="font-semibold">
                                  ${profitData.reduce((sum, i) => sum + i.consultant_cost, 0).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span>Non-Billable Expenses:</span>
                                <span className="font-semibold">
                                  ${profitData.reduce((sum, i) => sum + i.non_billable_expenses, 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 p-4 bg-utis-sage/10 rounded-lg border border-utis-sage">
                          <p className="text-sm text-utis-forest">
                            <strong>Formula:</strong> Profit = Revenue (Invoices) - Consultant Costs - Non-Billable Expenses
                            <br />
                            <strong>Note:</strong> Billable expenses are typically included in invoiced revenue. Only non-billable expenses are deducted from profit.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {profitData.length === 0 && profitPeriodStart && profitPeriodEnd && !loading && (
                  <div className="text-center py-12 text-gray-500">
                    No profit data found for the selected period.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mentoring Compensation Tab */}
        <TabsContent value="mentoring" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-utis-forest" />
                Mentoring Compensation Report
              </CardTitle>
              <CardDescription>Senior consultants earn compensation based on junior's hours × mentoring rate</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Period Start</Label>
                    <Input
                      type="date"
                      value={mentoringPeriodStart}
                      onChange={(e) => setMentoringPeriodStart(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Period End</Label>
                    <Input
                      type="date"
                      value={mentoringPeriodEnd}
                      onChange={(e) => setMentoringPeriodEnd(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Senior Consultant</Label>
                    <Select value={selectedSenior} onValueChange={setSelectedSenior}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Seniors</SelectItem>
                        {consultants.map(consultant => (
                          <SelectItem key={consultant.id} value={consultant.id}>
                            {consultant.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      onClick={loadMentoringCompensation}
                      disabled={loading}
                      className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white"
                    >
                      {loading ? 'Loading...' : 'Generate Report'}
                    </Button>
                  </div>
                </div>

                {mentoringData.length > 0 && (
                  <div>
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <Card className="border-purple-200 bg-purple-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-purple-900">Total Compensation</p>
                              <p className="text-2xl font-bold text-purple-900 mt-2">
                                ${mentoringData.reduce((sum, s) => sum + s.total_compensation, 0).toLocaleString()}
                              </p>
                            </div>
                            <DollarSign className="w-8 h-8 text-purple-700" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-blue-200 bg-blue-50">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-blue-900">Total Junior Hours</p>
                              <p className="text-2xl font-bold text-blue-900 mt-2">
                                {mentoringData.reduce((sum, s) => sum + s.total_junior_hours, 0).toFixed(1)}
                              </p>
                            </div>
                            <Clock className="w-8 h-8 text-blue-700" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="border-utis-sage bg-utis-sage/10">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-utis-forest">Active Seniors</p>
                              <p className="text-2xl font-bold text-utis-forest mt-2">
                                {mentoringData.length}
                              </p>
                            </div>
                            <Users className="w-8 h-8 text-utis-forest" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Generate Expenses Button */}
                    <div className="mb-4">
                      <Button
                        onClick={generateMentoringExpenses}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <DollarSign className="w-4 h-4 mr-2" />
                        {loading ? 'Generating...' : 'Generate Expenses for Mentoring'}
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">
                        Automatically create Direct expenses for each senior consultant's mentoring compensation
                      </p>
                    </div>

                    {/* Detailed Breakdown */}
                    {mentoringData.map((senior, idx) => (
                      <Card key={idx} className="mb-4">
                        <CardHeader className="bg-utis-sage/10">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-utis-forest">{senior.senior_name}</CardTitle>
                              <CardDescription>
                                Mentoring Rate: ${senior.mentoring_rate}/hr | 
                                Supervising {senior.juniors_supervised} consultant{senior.juniors_supervised > 1 ? 's' : ''}
                              </CardDescription>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-utis-forest">
                                ${senior.total_compensation.toLocaleString()}
                              </p>
                              <p className="text-sm text-gray-600">
                                {senior.total_junior_hours.toFixed(1)} junior hours
                              </p>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-4">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Junior Consultant</TableHead>
                                <TableHead>Hours Worked</TableHead>
                                <TableHead>Mentoring Rate</TableHead>
                                <TableHead>Compensation</TableHead>
                                <TableHead>Projects</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {senior.junior_details.map((junior, jIdx) => (
                                <TableRow key={jIdx}>
                                  <TableCell className="font-medium">{junior.junior_name}</TableCell>
                                  <TableCell>{junior.hours_worked.toFixed(1)} hrs</TableCell>
                                  <TableCell>${junior.mentoring_rate}/hr</TableCell>
                                  <TableCell className="font-bold text-purple-700">
                                    ${junior.compensation.toLocaleString()}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex flex-wrap gap-1">
                                      {junior.projects.map((project, pIdx) => (
                                        <Badge key={pIdx} variant="outline" className="text-xs">
                                          {project}
                                        </Badge>
                                      ))}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    ))}

                    <div className="mt-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-purple-900">
                        <strong>Formula:</strong> Mentoring Compensation = Junior's Hours × Senior's Mentoring Rate
                        <br />
                        <strong>Note:</strong> Senior consultants earn mentoring compensation in addition to their regular pay when supervising junior consultants.
                      </p>
                    </div>
                  </div>
                )}

                {mentoringData.length === 0 && mentoringPeriodStart && mentoringPeriodEnd && !loading && (
                  <div className="text-center py-12 text-gray-500">
                    No mentoring compensation data found for the selected period.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
